import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo, showSuccess } from "../utils/toast";
import {
  ArrowLeft, CheckCircle2, XCircle, User, Mail, Phone,
  Building2, Tag as TagIcon, DollarSign, CalendarClock,
  MessageSquare, Save, Pencil, X, Paperclip, Upload, Download, Trash
} from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../sass/pages/leadDetail.scss";

import HelpBar from "../components/HelpBar";
import HelpModal from "../components/HelpModal";

const StatusOpts = ["NOVO","QUALIFICANDO","PROPOSTA","GANHO","PERDIDO"];

// Mesmos valores aceitos no backend
const CanalOpts  = [
  { value: "SITE",      label: "Site" },
  { value: "WHATSAPP",  label: "WhatsApp" },
  { value: "PHONE",     label: "Telefone" },
  { value: "INDICACAO", label: "Indicação" },
  { value: "OUTRO",     label: "Outro" },
];

const canalLabel = (value) => {
  if (!value) return "-";
  const opt = CanalOpts.find((c) => c.value === value);
  return opt ? opt.label : value;
};

const statusColor = (st) => ({
  NOVO: "#64748b",
  QUALIFICANDO: "#0a66c2",
  PROPOSTA: "#f59e0b",
  GANHO: "#10b981",
  PERDIDO: "#ef4444",
}[st] || "#64748b");

const fmtBytes = (n=0) => {
  const b = Number(n)||0;
  if (b < 1024) return `${b} B`;
  if (b < 1024**2) return `${(b/1024).toFixed(1)} KB`;
  if (b < 1024**3) return `${(b/1024**2).toFixed(1)} MB`;
  return `${(b/1024**3).toFixed(1)} GB`;
};

export default function LeadDetail() {
  const nav = useNavigate();
  const { id } = useParams();

  const meId = Number(
    sessionStorage.getItem("IdFuncionario") ?? sessionStorage.getItem("codFunc") ?? 0
  ) || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const [lead, setLead]   = useState(null);
  const [edit, setEdit]   = useState(false);
  const [draft, setDraft] = useState(null);

  // Anexos
  const [anexos, setAnexos] = useState([]);
  const [file, setFile] = useState(null);
  const [descAnexo, setDescAnexo] = useState("");
  const [catAnexo, setCatAnexo] = useState("");

  // Notas
  const [notas, setNotas] = useState([]);
  const [loadingNotas, setLoadingNotas] = useState(false);
  const [nota, setNota] = useState("");
  const notaRef = useRef(null);

  // Ajuda
  const [helpOpen, setHelpOpen] = useState(false);

  const toNumberOrNull = (v) => {
    if (v === "" || v == null) return null;
    const s = String(v).replace(/\./g, "").replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data || null);
      setDraft(data || null);
    } catch {
      showError("Erro ao carregar lead");
      setLead(null);
      setDraft(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAnexos = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${id}/anexos`);
      setAnexos(Array.isArray(data) ? data : []);
    } catch {
      setAnexos([]);
      showError("Falha ao listar anexos");
    }
  }, [id]);

  const loadNotas = useCallback(async () => {
    setLoadingNotas(true);
    try {
      const { data } = await api.get(`/leads/${id}/notas`);
      setNotas(Array.isArray(data) ? data : []);
    } catch {
      setNotas([]);
      showError("Falha ao carregar notas");
    } finally {
      setLoadingNotas(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAnexos(); }, [loadAnexos]);
  useEffect(() => { loadNotas(); }, [loadNotas]);

  const startEdit  = () => { setDraft(lead); setEdit(true);  };
  const cancelEdit = () => { setDraft(lead); setEdit(false); };

  const patchField = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  const buildPatchPayload = (before, after) => {
    const map = {
      NOME: "nome",
      CONTATO_NOME: "contatoNome",
      CONTATO_EMAIL: "contatoEmail",
      CONTATO_TEL: "contatoTel",
      CANAL: "canal",
      VALOR_ESTIMADO: "valorEstimado",
      TAGS: "tags",
      DESCRICAO: "descricao",
    };
    const out = {};
    Object.entries(map).forEach(([src, dst]) => {
      if (after[src] !== before[src]) {
        out[dst] = src === "VALOR_ESTIMADO"
          ? toNumberOrNull(after[src])
          : (after[src] ?? null);
      }
    });
    return out;
  };

  const saveFields = async () => {
    if (!draft || !lead) return;
    const payload = buildPatchPayload(lead, draft);
    if (Object.keys(payload).length === 0) { setEdit(false); return; }

    setSaving(true);
    const prev = lead;
    setLead((l) => ({ ...l, ...draft }));
    try {
      await api.patch(`/leads/${id}`, payload);
      showInfo("Alterações salvas");
      setEdit(false);
    } catch {
      setLead(prev);
      showError("Falha ao salvar alterações");
    } finally {
      setSaving(false);
    }
  };

  const mudarStatus = async (status) => {
    if (!lead || status === lead.STATUS) return;
    const prev = lead.STATUS;
    setLead((l) => ({ ...l, STATUS: status }));
    try {
      const body = { status, autorId: meId };
      if (lead.VALOR_ESTIMADO != null) body.valor = toNumberOrNull(lead.VALOR_ESTIMADO);
      await api.patch(`/leads/${id}/status`, body);
      showInfo("Status atualizado");
    } catch {
      setLead((l) => ({ ...l, STATUS: prev }));
      showError("Erro ao atualizar status");
    }
  };

  const atribuirMe = async () => {
    if (!meId) { showError("Sem ID de usuário"); return; }
    try {
      await api.post(`/leads/${id}/atribuir`, { ownerId: meId });
      showInfo("Atribuído a você");
      await load();
    } catch {
      showError("Erro ao atribuir");
    }
  };

  const salvarNota = async () => {
    const texto = (nota || "").trim();
    if (!texto) return;
    try {
      await api.post(`/leads/${id}/notas`, { texto, autorId: meId });
      setNota("");
      showInfo("Nota adicionada");
      await loadNotas();
      notaRef.current?.focus();
    } catch {
      showError("Erro ao salvar nota");
    }
  };

  const onDeleteNota = async (n) => {
    if (!window.confirm("Remover esta nota?")) return;
    try {
      await api.delete(`/leads/${id}/notas/${n.ID_NOTA}`);
      showInfo("Nota removida");
      await loadNotas();
    } catch {
      showError("Falha ao remover nota");
    }
  };

  // anexos
  const onUploadAnexo = async (e) => {
    e.preventDefault();
    if (!file) return showError("Selecione um arquivo");
    const fd = new FormData();
    fd.append("file", file);
    if (descAnexo) fd.append("descricao", descAnexo);
    if (catAnexo)  fd.append("categoria", catAnexo);
    try {
      await api.post(`/leads/${id}/anexos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showSuccess("Anexo enviado");
      setFile(null); setDescAnexo(""); setCatAnexo("");
      await loadAnexos();
    } catch (err) {
      const code = err?.response?.data?.message;
      if (code === "FILE_TOO_LARGE") showError("Arquivo maior que 25MB.");
      else if (code === "UNSUPPORTED_MEDIA_TYPE") showError("Tipo de arquivo não permitido.");
      else if (code === "INVALID_FILE") showError("Arquivo inválido.");
      else showError("Falha no upload.");
    }
  };

  const onDeleteAnexo = async (a) => {
    if (!window.confirm("Remover este anexo?")) return;
    try {
      await api.delete(`/leads/${id}/anexos/${a.ID_ANEXO}`);
      showInfo("Anexo removido");
      await loadAnexos();
    } catch {
      showError("Falha ao remover anexo");
    }
  };

  const onDownloadAnexo = async (a) => {
    try {
      const { data } = await api.get(`/leads/anexos/${a.ID_ANEXO}/download`);
      window.open(data.url, "_blank");
    } catch {
      showError("Falha ao gerar link de download");
    }
  };

  const statusStyle = useMemo(() => ({
    background: "rgba(255,255,255,.08)",
    border: `1px solid ${statusColor(lead?.STATUS || "NOVO")}55`,
    color: statusColor(lead?.STATUS || "NOVO"),
  }), [lead?.STATUS]);

  // atalhos
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "?") { setHelpOpen((v) => !v); return; }
      if (k === "e") { if (!edit) startEdit(); return; }
      if (k === "s") { if (edit) { e.preventDefault(); saveFields(); } return; }
      if (k === "escape") { if (edit) cancelEdit(); return; }
      if (k === "g") { mudarStatus("GANHO"); return; }
      if (k === "p") { mudarStatus("PERDIDO"); return; }
      if (k === "a") { atribuirMe(); return; }
      if (k === "n") { notaRef.current?.focus(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [edit, saveFields, cancelEdit, mudarStatus]);

  // loading / empty
  if (loading) {
    return (
      <MainLayout>
        <div className="lead-details page-padded" style={{ padding: "18px 20px 28px" }}>
          <div className="lead-header">
            <div className="left">
              <button className="btn-ghost" onClick={() => nav(-1)}>
                <ArrowLeft size={18} /> Voltar
              </button>
            </div>
          </div>
          <div className="lead-body">
            <Skeleton height={60} style={{ marginBottom: 12 }} />
            <Skeleton height={180} style={{ marginBottom: 16 }} />
            <Skeleton height={300} />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!lead) {
    return (
      <MainLayout>
        <div className="lead-details page-padded" style={{ padding: "18px 20px 28px" }}>
          <div className="lead-header">
            <div className="left">
              <button className="btn-ghost" onClick={() => nav(-1)}>
                <ArrowLeft size={18} /> Voltar
              </button>
            </div>
          </div>
          <div className="empty-state">
            <XCircle size={22} />
            <p>Lead não encontrado.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="lead-details page-padded" style={{ padding: "18px 20px 28px" }}>
        <div className="lead-header">
          <div className="left">
            <button className="btn-ghost" onClick={() => nav(-1)}>
              <ArrowLeft size={18} /> Voltar
            </button>
            <div className="title-wrap">
              <h1 className="title">{lead.NOME || "Sem nome"}</h1>
              <div className="subtitle">
                <span className="mono">#{lead.CODIGO}</span>
                <span className="dot">•</span>
                <span><CalendarClock size={14}/> {new Date(lead.CREATED_AT).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="right">
            <div className="status-inline" style={statusStyle}>{lead.STATUS}</div>

            <select
              className="select"
              value={lead.STATUS}
              onChange={(e) => mudarStatus(e.target.value)}
              title="Alterar status"
            >
              {StatusOpts.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            <button
              className="btn"
              onClick={atribuirMe}
              title="Atribuir p/ mim"
              disabled={Number(lead.OWNER_ID || 0) === Number(meId || -1)}
            >
              <User size={16}/>
              {Number(lead.OWNER_ID || 0) === Number(meId || -1) ? "Já atribuído" : "Atribuir"}
            </button>

            {!edit && (
              <button className="btn primary" onClick={startEdit}>
                <Pencil size={16}/> Editar
              </button>
            )}
            {edit && (
              <>
                <button className="btn" onClick={cancelEdit}>
                  <X size={16}/> Cancelar
                </button>
                <button className="btn success" disabled={saving} onClick={saveFields}>
                  <Save size={16}/> {saving ? "Salvando..." : "Salvar"}
                </button>
              </>
            )}
          </div>
        </div>

        <HelpBar
          onOpen={() => setHelpOpen(true)}
        >
          Edite dados, adicione notas, anexos e altere o status. Atalhos: ? ajuda · E editar · S salvar · Esc cancelar · G ganho · P perdido · A atribuir · N nota.
        </HelpBar>

        <div className="lead-body">
          {/* Dados */}
          <div className="lead-card">
            <div className="card-title">Dados do Lead</div>

            <div className="form-grid">
              <div className="field">
                <label><Building2 size={14}/> Empresa/Org.</label>
                {!edit ? (
                  <div className="value">{lead.NOME || "-"}</div>
                ) : (
                  <input
                    value={draft?.NOME || ""}
                    onChange={(e) => patchField("NOME", e.target.value)}
                    placeholder="Empresa ou organização"
                  />
                )}
              </div>

              <div className="field">
                <label><User size={14}/> Contato</label>
                {!edit ? (
                  <div className="value">{lead.CONTATO_NOME || "-"}</div>
                ) : (
                  <input
                    value={draft?.CONTATO_NOME || ""}
                    onChange={(e) => patchField("CONTATO_NOME", e.target.value)}
                    placeholder="Nome do contato"
                  />
                )}
              </div>

              <div className="field">
                <label><Mail size={14}/> E-mail</label>
                {!edit ? (
                  <div className="value">{lead.CONTATO_EMAIL || "-"}</div>
                ) : (
                  <input
                    value={draft?.CONTATO_EMAIL || ""}
                    onChange={(e) => patchField("CONTATO_EMAIL", e.target.value)}
                    placeholder="email@dominio.com"
                  />
                )}
              </div>

              <div className="field">
                <label><Phone size={14}/> Telefone</label>
                {!edit ? (
                  <div className="value">{lead.CONTATO_TEL || "-"}</div>
                ) : (
                  <input
                    value={draft?.CONTATO_TEL || ""}
                    onChange={(e) => patchField("CONTATO_TEL", e.target.value)}
                    placeholder="(00) 00000-0000"
                  />
                )}
              </div>

              <div className="field">
                <label><TagIcon size={14}/> Canal</label>
                {!edit ? (
                  <div className="value">{canalLabel(lead.CANAL)}</div>
                ) : (
                  <select
                    value={draft?.CANAL || ""}
                    onChange={(e) => patchField("CANAL", e.target.value)}
                  >
                    <option value="">Selecione…</option>
                    {CanalOpts.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="field">
                <label><DollarSign size={14}/> Valor Estimado</label>
                {!edit ? (
                  <div className="value">
                    {lead.VALOR_ESTIMADO != null
                      ? Number(lead.VALOR_ESTIMADO).toLocaleString("pt-BR", { style:"currency", currency:"BRL" })
                      : "-"}
                  </div>
                ) : (
                  <input
                    inputMode="decimal"
                    value={draft?.VALOR_ESTIMADO ?? ""}
                    onChange={(e) => patchField("VALOR_ESTIMADO", e.target.value)}
                    placeholder="0,00"
                  />
                )}
              </div>

              <div className="field span-2">
                <label><TagIcon size={14}/> Tags</label>
                {!edit ? (
                  <div className="value">{lead.TAGS || "-"}</div>
                ) : (
                  <input
                    value={draft?.TAGS || ""}
                    onChange={(e) => patchField("TAGS", e.target.value)}
                    placeholder="tag1, tag2, tag3"
                  />
                )}
              </div>

              <div className="field span-2">
                <label><MessageSquare size={14}/> Descrição</label>
                {!edit ? (
                  <div className="value multi">{lead.DESCRICAO || "-"}</div>
                ) : (
                  <textarea
                    rows={4}
                    value={draft?.DESCRICAO || ""}
                    onChange={(e) => patchField("DESCRICAO", e.target.value)}
                    placeholder="Contexto do lead, dores, necessidades…"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Anexos */}
          <div className="lead-card">
            <div className="card-title"><Paperclip size={16}/> Anexos</div>

            <form className="anexo-form" onSubmit={onUploadAnexo} style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
              <input
                type="file"
                onChange={(e)=>setFile(e.target.files?.[0] || null)}
                accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.xlsm,.txt,.csv"
                title="Selecione um arquivo (até 25MB)"
              />
              <input
                placeholder="Descrição (opcional)"
                value={descAnexo}
                onChange={(e)=>setDescAnexo(e.target.value)}
                maxLength={300}
                style={{ flex:"1 1 220px" }}
              />
              <input
                placeholder="Categoria (opcional)"
                value={catAnexo}
                onChange={(e)=>setCatAnexo(e.target.value)}
                maxLength={60}
                style={{ width:180 }}
              />
              <button className="btn primary" type="submit" title="Enviar anexo">
                <Upload size={16}/> Enviar
              </button>
            </form>

            <div className="table-wrap" style={{ marginTop: 10 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>MIME</th>
                    <th>Tam.</th>
                    <th>Descrição</th>
                    <th>Categoria</th>
                    <th>Criado em</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {anexos.map(a => (
                    <tr key={a.ID_ANEXO}>
                      <td>{a.ID_ANEXO}</td>
                      <td>{a.NOME_ORIGINAL}</td>
                      <td>{a.MIMETYPE || "-"}</td>
                      <td>{fmtBytes(a.TAMANHO)}</td>
                      <td>{a.DESCRICAO || "-"}</td>
                      <td>{a.CATEGORIA || "-"}</td>
                      <td>{a.CREATED_AT ? new Date(a.CREATED_AT).toLocaleString("pt-BR") : "-"}</td>
                      <td style={{ whiteSpace:"nowrap" }}>
                        <button className="btn ghost" onClick={()=>onDownloadAnexo(a)} title="Baixar"><Download size={14}/></button>
                        <button className="btn ghost" onClick={()=>onDeleteAnexo(a)} title="Remover"><Trash size={14}/></button>
                      </td>
                    </tr>
                  ))}
                  {anexos.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign:"center" }}>Sem anexos</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notas */}
          <div className="lead-card">
            <div className="card-title">Notas</div>

            <div className="note-new">
              <input
                ref={notaRef}
                placeholder="Escreva uma nota e pressione Enter…"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") salvarNota(); }}
              />
              <button className="btn" onClick={salvarNota}>
                <CheckCircle2 size={16}/> Adicionar
              </button>
            </div>

            <ul className="notes-list">
              {loadingNotas && (
                <li className="note empty">Carregando notas…</li>
              )}
              {!loadingNotas && notas.length === 0 && (
                <li className="note empty">Sem notas.</li>
              )}
              {!loadingNotas && notas.map((n) => (
                <li key={n.ID_NOTA} className="note">
                  <div className="note-head">
                    <span className="mono">#{n.ID_NOTA}</span>
                    <span className="dot">•</span>
                    <span className="muted">{new Date(n.CREATED_AT).toLocaleString()}</span>
                    <button
                      type="button"
                      className="btn ghost"
                      style={{ marginLeft: "auto" }}
                      onClick={() => onDeleteNota(n)}
                      title="Remover nota"
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                  <div className="note-body">{n.TEXTO}</div>
                </li>
              ))}
            </ul>
          </div>

          {/* Histórico (continua como estava; ainda não há backend específico) */}
          <div className="lead-card">
            <div className="card-title">Histórico</div>
            <ul className="notes-list">
              {(lead.updates || []).map((u) => (
                <li key={u.ID_UPDATE} className="note">
                  <div className="note-head">
                    <b>{u.TIPO}</b>
                    <span className="dot">•</span>
                    <span className="muted">{new Date(u.CREATED_AT).toLocaleString()}</span>
                  </div>
                  <div className="note-body">
                    {u.STATUS_NOVO && <span>Status: {u.STATUS_NOVO}</span>}
                    {u.DESCRICAO && <span> — {u.DESCRICAO}</span>}
                  </div>
                </li>
              ))}
              {(lead.updates || []).length === 0 && (
                <li className="note empty">Sem eventos.</li>
              )}
            </ul>
          </div>

          {/* Ações rápidas */}
          <div className="lead-actions-quick">
            <button
              className="btn success"
              disabled={lead.STATUS === "GANHO"}
              onClick={() => mudarStatus("GANHO")}
              title="Marcar como ganho"
            >
              <CheckCircle2 size={16}/> Marcar como GANHO
            </button>
            <button
              className="btn danger"
              disabled={lead.STATUS === "PERDIDO"}
              onClick={() => mudarStatus("PERDIDO")}
              title="Marcar como perdido"
            >
              <XCircle size={16}/> Marcar como PERDIDO
            </button>
          </div>
        </div>

        <HelpModal
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          title="Como usar — Detalhe do Lead"
          sections={[
            {
              title: "O que dá para fazer",
              items: [
                "Editar dados básicos do lead (empresa, contato, canal, valor, tags, descrição).",
                "Atribuir o lead a você.",
                "Mudar o status: NOVO, QUALIFICANDO, PROPOSTA, GANHO, PERDIDO.",
                "Adicionar notas e acompanhar o histórico de eventos.",
                "Enviar, baixar e remover anexos (PDF, imagens, Office, CSV/TXT)."
              ]
            },
            {
              title: "Atalhos de teclado",
              items: [
                "?: abrir/fechar ajuda",
                "E: editar • S: salvar • Esc: cancelar",
                "G: marcar como GANHO • P: marcar como PERDIDO",
                "A: atribuir para mim • N: focar campo de nota"
              ]
            }
          ]}
        />
      </div>
    </MainLayout>
  );
}
